import { Classification } from '@/types/classification';
import type { GeometryFilters } from '@/context/geometry';

/** First code from a dynamic filter (URL / API may use string or string[] for `in` filters). */
export function firstDynamicFilterCode(
  dynamicFilters: GeometryFilters['dynamicFilters'] | undefined,
  field: string
): string | undefined {
  const raw = dynamicFilters?.find((f) => f.field === field)?.value;
  if (raw === undefined || raw === null) {
    return undefined;
  }
  if (Array.isArray(raw)) {
    const first = raw[0];
    return first !== undefined && first !== null ? String(first) : undefined;
  }
  return String(raw);
}

export const filtersToQueryParams = (filters: GeometryFilters) => {
  const params = new URLSearchParams();
  if (filters.dynamicFilters) {
    filters.dynamicFilters.forEach((filter) => {
      const serialized = Array.isArray(filter.value)
        ? filter.value.join(',')
        : String(filter.value);
      params.set(filter.field, serialized);
    });
  }

  if (filters.dimensionLevelFilters && filters.dimensionLevelFilters.length > 0) {
    filters.dimensionLevelFilters.forEach((dimFilter) => {
      const dimensionKey = dimFilter.dimension_id === null ? 'global' : dimFilter.dimension_id;
      if (dimFilter.level_opportunity && dimFilter.level_opportunity.length > 0) {
        params.set(`d${dimensionKey}_opp`, dimFilter.level_opportunity.join(','));
      }
      if (dimFilter.level_risk && dimFilter.level_risk.length > 0) {
        params.set(`d${dimensionKey}_risk`, dimFilter.level_risk.join(','));
      }
    });
  }

  if (filters.additionalMunicipalityCodes && filters.additionalMunicipalityCodes.length > 0) {
    params.set('additionalMunicipalityCodes', filters.additionalMunicipalityCodes.join(','));
  }

  return params.toString();
};

export const parseFiltersFromSearchParams = (searchParams: URLSearchParams): GeometryFilters => {
  const region = searchParams.get('region') || undefined;
  const biome = searchParams.get('biome') || undefined;
  const state = searchParams.get('state') || undefined;
  const intermediateRegion = searchParams.get('intermediate_region') || undefined;
  const immediateRegion = searchParams.get('immediate_region') || undefined;
  const classification = searchParams.get('classification') as Classification | undefined;
  const dimensionId = searchParams.get('dimension_id')
    ? Number(searchParams.get('dimension_id'))
    : undefined;
  const dynamicFilters: GeometryFilters['dynamicFilters'] = [];
  const municipalityCode = searchParams.get('municipality_code') || undefined;
  const additionalMunicipalityCodesParam = searchParams.get('additionalMunicipalityCodes');
  const additionalMunicipalityCodes = additionalMunicipalityCodesParam
    ? additionalMunicipalityCodesParam.split(',').filter(Boolean)
    : undefined;

  if (region) dynamicFilters.push({ field: 'region', operator: 'eq', value: region });
  if (biome) dynamicFilters.push({ field: 'biome', operator: 'in', value: biome });
  if (state) dynamicFilters.push({ field: 'state', operator: 'eq', value: state });
  if (intermediateRegion) {
    const codes = intermediateRegion.split(',').filter(Boolean);
    if (codes.length > 0) {
      dynamicFilters.push({
        field: 'intermediate_region',
        operator: 'in',
        value: codes,
      });
    }
  }
  if (immediateRegion) {
    const codes = immediateRegion.split(',').filter(Boolean);
    if (codes.length > 0) {
      dynamicFilters.push({
        field: 'immediate_region',
        operator: 'in',
        value: codes,
      });
    }
  }
  if (classification)
    dynamicFilters.push({
      field: 'classification',
      operator: 'eq',
      value: classification,
    });
  if (dimensionId)
    dynamicFilters.push({
      field: 'dimension_id',
      operator: 'eq',
      value: dimensionId,
    });
  if (municipalityCode)
    dynamicFilters.push({
      field: 'municipality_code',
      operator: 'eq',
      value: municipalityCode,
    });

  const dimensionFiltersMap = new Map<
    string,
    {
      dimension_id: number | null;
      level_opportunity: string[];
      level_risk: string[];
    }
  >();

  searchParams.forEach((value, key) => {
    const oppMatch = key.match(/^d(\d+|global)_opp$/);
    const riskMatch = key.match(/^d(\d+|global)_risk$/);

    if (oppMatch) {
      const dimensionIdStr = oppMatch[1];
      const dimensionId = dimensionIdStr === 'global' ? null : Number(dimensionIdStr);
      const levels = value.split(',');
      const mapKey = dimensionIdStr;

      if (!dimensionFiltersMap.has(mapKey)) {
        dimensionFiltersMap.set(mapKey, {
          dimension_id: dimensionId,
          level_opportunity: [],
          level_risk: [],
        });
      }

      const filter = dimensionFiltersMap.get(mapKey);
      if (filter) {
        filter.level_opportunity = levels;
      }
    }

    if (riskMatch) {
      const dimensionIdStr = riskMatch[1];
      const dimensionId = dimensionIdStr === 'global' ? null : Number(dimensionIdStr);
      const levels = value.split(',');
      const mapKey = dimensionIdStr;

      if (!dimensionFiltersMap.has(mapKey)) {
        dimensionFiltersMap.set(mapKey, {
          dimension_id: dimensionId,
          level_opportunity: [],
          level_risk: [],
        });
      }

      const filter = dimensionFiltersMap.get(mapKey);
      if (filter) {
        filter.level_risk = levels;
      }
    }
  });

  const dimensionLevelFilters =
    dimensionFiltersMap.size > 0 ? Array.from(dimensionFiltersMap.values()) : undefined;

  return { dynamicFilters, dimensionLevelFilters, additionalMunicipalityCodes };
};

/**
 * Prefer dimension_id from URL (updated immediately on layer change) over React state,
 * which can lag one frame after navigate — avoids refetch using a stale layer.
 */
export function getEffectiveDimensionLayerFromUrlAndState(
  urlFilters: GeometryFilters,
  selectedLayer: number
): number {
  const fromUrl = urlFilters.dynamicFilters?.find((f) => f.field === 'dimension_id')?.value as
    | number
    | undefined;
  return fromUrl !== undefined ? fromUrl : selectedLayer;
}

/** Sets or clears dimension_id on URL filters; preserves other dynamicFilters and dimensionLevelFilters. */
export function applyDimensionLayerToFilters(
  current: GeometryFilters,
  layerValue: number
): GeometryFilters {
  const withoutDimension = (current.dynamicFilters ?? []).filter((f) => f.field !== 'dimension_id');

  if (layerValue === -10) {
    return {
      ...current,
      dynamicFilters: withoutDimension,
    };
  }

  return {
    ...current,
    dynamicFilters: [
      ...withoutDimension,
      { field: 'dimension_id', operator: 'eq', value: layerValue },
    ],
  };
}

/**
 * Remove municipality_code parameter from current URL
 */
export const removeMunicipalityCodeFromUrl = () => {
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);

  if (params.has('municipality_code')) {
    params.delete('municipality_code');
    const newUrl = `${url.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    window.history.replaceState({}, '', newUrl);
  }
};
